import type {
    VectorizeTask,
    VectorizeTaskList,
    VectorizeTaskMeta,
    VectorizeTaskParameters,
    VectorizeTaskResult,
    VectorizeTaskStreamEvent,
} from "~/lib/api/types/vectorizeTasks";
import { readEventStream } from "~/lib/sse/readEventStream";

/**
 * Who asks: the merchant token and the app slug. The Nuxt server checks the
 * token against the merchant API and derives the queue's owner from it —
 * the browser never names the owner itself.
 */
export type VectorizeTaskAuth = { slug: string; token: string };

const BASE = "/api/vectorizeTasks";

export class VectorizeTaskRequestError extends Error {
    constructor(
        message: string,
        readonly status: number,
    ) {
        super(message);
        this.name = "VectorizeTaskRequestError";
    }
}

/** Stops a running task; it ends as `cancelled` and the next one starts. */
export async function cancelVectorizeTask(
    auth: VectorizeTaskAuth,
    id: string,
): Promise<VectorizeTask> {
    const response = await request(auth, `/${encodeURIComponent(id)}/cancel`, {
        method: "POST",
    });

    return (await response.json()) as VectorizeTask;
}

export async function deleteVectorizeTask(
    auth: VectorizeTaskAuth,
    id: string,
): Promise<void> {
    await request(auth, `/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export async function enqueueVectorizeTask(
    auth: VectorizeTaskAuth,
    file: File,
    parameters: VectorizeTaskParameters,
    meta: VectorizeTaskMeta,
): Promise<VectorizeTask> {
    const form = new FormData();

    form.append("file", file, file.name);
    form.append("params", JSON.stringify(parameters));
    form.append("meta", JSON.stringify(meta));

    const response = await request(auth, "", { body: form, method: "POST" });

    return (await response.json()) as VectorizeTask;
}

/** The input of a task, e.g. to show it as the first frame of the player. */
export async function fetchVectorizeTaskFile(
    auth: VectorizeTaskAuth,
    id: string,
    name: "source" | "thumbnail",
): Promise<Blob> {
    const response = await request(
        auth,
        `/${encodeURIComponent(id)}/files/${name}`,
    );

    return response.blob();
}

export async function fetchVectorizeTaskResult(
    auth: VectorizeTaskAuth,
    id: string,
): Promise<VectorizeTaskResult> {
    const response = await request(auth, `/${encodeURIComponent(id)}/result`);

    return (await response.json()) as VectorizeTaskResult;
}

export async function fetchVectorizeTasks(
    auth: VectorizeTaskAuth,
): Promise<VectorizeTaskList> {
    const response = await request(auth, "");

    return (await response.json()) as VectorizeTaskList;
}

/**
 * Follows a task: every event of its current attempt from the start, then
 * live, until `result`, `error` or `cancelled`. Aborting only stops watching.
 */
export async function followVectorizeTask(
    auth: VectorizeTaskAuth,
    id: string,
    signal: AbortSignal,
    onEvent: (event: VectorizeTaskStreamEvent) => void,
): Promise<"ended" | "finished"> {
    const response = await request(auth, `/${encodeURIComponent(id)}/events`, {
        headers: { Accept: "text/event-stream" },
        signal,
    });

    for await (const event of readEventStream<VectorizeTaskStreamEvent>(
        response,
    )) {
        onEvent(event);

        if (
            event.type === "result" ||
            event.type === "error" ||
            event.type === "cancelled"
        ) {
            return "finished";
        }
    }

    // The stream closed early — an image-processor instance shutting down.
    return "ended";
}

export async function reorderVectorizeTasks(
    auth: VectorizeTaskAuth,
    ids: string[],
): Promise<VectorizeTaskList> {
    const response = await request(auth, "/order", {
        body: JSON.stringify({ ids }),
        headers: { "Content-Type": "application/json" },
        method: "PUT",
    });

    return (await response.json()) as VectorizeTaskList;
}

export async function retryVectorizeTask(
    auth: VectorizeTaskAuth,
    id: string,
): Promise<VectorizeTask> {
    const response = await request(auth, `/${encodeURIComponent(id)}/retry`, {
        method: "POST",
    });

    return (await response.json()) as VectorizeTask;
}

async function readMessage(response: Response): Promise<string | undefined> {
    try {
        const body = (await response.json()) as { message?: string };

        return body.message;
    } catch {
        return;
    }
}

async function request(
    auth: VectorizeTaskAuth,
    path: string,
    init: RequestInit = {},
): Promise<Response> {
    const response = await fetch(`${BASE}${path}`, {
        ...init,
        headers: {
            ...(init.headers as Record<string, string> | undefined),
            Authorization: `Bearer ${auth.token}`,
            "X-App-Slug": auth.slug,
        },
    });

    if (!response.ok) {
        throw new VectorizeTaskRequestError(
            (await readMessage(response)) ??
                `Vectorization task request failed (${String(response.status)})`,
            response.status,
        );
    }

    return response;
}
